import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TuftedRugModal from '../components/TuftedRugModal';
import MediaLightbox from '../components/MediaLightbox';
import '../styles/TuftingPage.css';
import { useLanguage } from '../i18n/LanguageContext';

const PRODUCTS_PUBLIC_ENDPOINT = '/.netlify/functions/products-public';

const normalizeMediaUrl = (value) => {
    const raw = String(value || '').trim();
    if (!raw) {
        return '';
    }
    if (
        raw.includes('/product-media/') &&
        !raw.includes('/.netlify/functions/product-media/')
    ) {
        return raw.replace(
            '/product-media/',
            '/.netlify/functions/product-media/'
        );
    }
    return raw;
};

const isVideoUrl = (value) => {
    const decoded = decodeURIComponent(String(value || '').toLowerCase());
    return (
        decoded.includes('.mp4') ||
        decoded.includes('.mov') ||
        decoded.includes('.webm') ||
        decoded.includes('.m4v')
    );
};

const pickRandomItems = (items, count) => {
    if (!Array.isArray(items) || !items.length || count <= 0) {
        return [];
    }
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, Math.min(count, copy.length));
};

function TuftingPage() {
    const { t, language } = useLanguage();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeImageIndex, setActiveImageIndex] = useState({});
    const [availabilityFilter, setAvailabilityFilter] = useState('all');
    const [products, setProducts] = useState([]);
    const [toastMessage, setToastMessage] = useState('');
    const [isToastVisible, setIsToastVisible] = useState(false);
    const toastTimeoutRef = useRef(null);
    const steps = t('tufting.process.steps');
    const locale = language === 'en' ? 'en-GB' : 'fr-FR';
    const priceFormatter = useMemo(
        () =>
            new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0
            }),
        [locale]
    );

    const [lightboxState, setLightboxState] = useState({
        open: false,
        items: [],
        index: 0
    });

    const getPriceLabel = (price) => {
        if (!price) {
            return t('tufting.quote');
        }
        return priceFormatter.format(price);
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const showToast = (message) => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }
        setToastMessage(message);
        setIsToastVisible(true);
        toastTimeoutRef.current = setTimeout(() => {
            setIsToastVisible(false);
        }, 3200);
    };

    useEffect(() => {
        return () => {
            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        let ignore = false;
        const fetchProducts = async () => {
            try {
                const res = await fetch(
                    `${PRODUCTS_PUBLIC_ENDPOINT}?category=tapis&order=created_asc`
                );
                if (!res.ok) {
                    return;
                }
                const payload = await res.json();
                if (ignore) {
                    return;
                }
                const next = (payload.items || []).map((item) => ({
                    id: item.slug || String(item.id),
                    title: item.title,
                    price: item.price,
                    widthCm: item.width_cm,
                    heightCm: item.height_cm,
                    isSold: Boolean(item.is_sold),
                    buyLink: item.buy_link || '',
                    media: Array.isArray(item.media)
                        ? item.media.map(normalizeMediaUrl).filter(Boolean)
                        : []
                }));
                setProducts(next);
            } catch {
                // keep page usable even if API is unavailable
            }
        };
        fetchProducts();
        return () => {
            ignore = true;
        };
    }, []);

    const handleThumbnailClick = (id, index) => {
        setActiveImageIndex((prev) => ({ ...prev, [id]: index }));
    };

    const openLightbox = (items, index) => {
        if (!items.length) {
            return;
        }
        setLightboxState({
            open: true,
            items,
            index
        });
    };

    const closeLightbox = () => {
        setLightboxState((prev) => ({ ...prev, open: false }));
    };

    const navigateLightbox = (direction) => {
        setLightboxState((prev) => {
            const count = prev.items.length;
            if (!count) {
                return prev;
            }

            const delta = direction === 'next' ? 1 : -1;
            const nextIndex =
                ((prev.index + delta) % count + count) % count;

            return {
                ...prev,
                index: nextIndex
            };
        });
    };

    const filteredTufting = products.filter((item) => {
        if (availabilityFilter === 'available') {
            return !item.isSold;
        }
        if (availabilityFilter === 'sold') {
            return item.isSold;
        }
        return true;
    });

    const isSingleProduct = filteredTufting.length === 1;
    const heroMedia = useMemo(() => {
        return pickRandomItems(
            products
            .flatMap((item) => item.media || [])
            .filter((url) => !isVideoUrl(url))
            .filter(Boolean),
            4
        );
    }, [products]);

    return (
        <div className='page tufting-page'>
            <Header />
            <main>
                <section className='hero tufting-hero'>
                    <div className='container hero-grid'>
                        <div className='hero-copy'>
                            <p className='eyebrow'>
                                {t('tufting.hero.eyebrow')}
                            </p>
                            <h1>{t('tufting.hero.title')}</h1>
                            <p className='lead'>
                                {t('tufting.hero.lead')}
                            </p>
                            <div className='hero-actions'>
                                <a
                                    className='button'
                                    href='mailto:lucasmafrica57@gmail.com'
                                >
                                    {t('tufting.hero.quote')}
                                </a>
                                <button
                                    type='button'
                                    className='button button-outline'
                                    onClick={handleOpenModal}
                                    data-track-label='Ouvrir modal tufting'
                                >
                                    {t('tufting.hero.estimate')}
                                </button>
                            </div>
                            <p className='hero-note'>
                                {t('tufting.hero.note')}{' '}
                                <Link className='text-link' to='/faq'>
                                    {t('tufting.hero.noteLink')}
                                </Link>
                            </p>
                        </div>
                        <div className='tufting-hero-visual'>
                            <div className='tufting-collage'>
                                {heroMedia[0] ? (
                                    <img
                                        src={heroMedia[0]}
                                        alt={t('tufting.collage.medievalAlt')}
                                        className='tufting-collage-rug tufting-collage-main'
                                    />
                                ) : null}
                                {heroMedia[1] ? (
                                    <img
                                        src={heroMedia[1]}
                                        alt={t('tufting.collage.lavaAlt')}
                                        className='tufting-collage-rug tufting-collage-secondary'
                                    />
                                ) : null}
                                {heroMedia[2] ? (
                                    <img
                                        src={heroMedia[2]}
                                        alt={t('tufting.collage.sunAlt')}
                                        className='tufting-collage-rug tufting-collage-tertiary'
                                    />
                                ) : null}
                                {heroMedia[3] ? (
                                    <img
                                        src={heroMedia[3]}
                                        alt={t('tufting.collage.floralAlt')}
                                        className='tufting-collage-rug tufting-collage-quaternary'
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                </section>

                <section className='section'>
                    <div className='container section-header'>
                        <div>
                            <p className='eyebrow'>
                                {t('tufting.process.eyebrow')}
                            </p>
                            <h2>{t('tufting.process.title')}</h2>
                        </div>
                        <p className='section-note'>
                            {t('tufting.process.note')}
                        </p>
                    </div>
                    <div className='container process-grid'>
                        {steps.map((step, index) => (
                            <div
                                key={step.title}
                                className='card process-card reveal'
                                style={{ '--delay': `${index * 0.12}s` }}
                            >
                                <span className='step-index'>0{index + 1}</span>
                                <h3>{step.title}</h3>
                                <p>{step.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className='section product-section'>
                    <div className='container section-header'>
                        <div>
                            <p className='eyebrow'>
                                {t('tufting.library.eyebrow')}
                            </p>
                            <h2>{t('tufting.library.title')}</h2>
                        </div>
                        <p className='section-note'>
                            {t('tufting.library.note')}
                        </p>
                    </div>
                    <div className='container filter-bar'>
                        <button
                            type='button'
                            className={`filter-button ${
                                availabilityFilter === 'all' ? 'active' : ''
                            }`}
                            onClick={() => setAvailabilityFilter('all')}
                            data-track-label='Afficher toutes les pieces tufting'
                        >
                            {t('tufting.filters.all')}
                        </button>
                        <button
                            type='button'
                            className={`filter-button ${
                                availabilityFilter === 'available'
                                    ? 'active'
                                    : ''
                            }`}
                            onClick={() =>
                                setAvailabilityFilter('available')
                            }
                            data-track-label='Afficher disponibles tufting'
                        >
                            {t('tufting.filters.available')}
                        </button>
                        <button
                            type='button'
                            className={`filter-button ${
                                availabilityFilter === 'sold' ? 'active' : ''
                            }`}
                            onClick={() => setAvailabilityFilter('sold')}
                            data-track-label='Afficher vendus tufting'
                        >
                            {t('tufting.filters.sold')}
                        </button>
                    </div>
                    <div
                        className={`container product-grid${
                            isSingleProduct ? ' is-single' : ''
                        }`}
                    >
                        {filteredTufting.map((item, index) => {
                            const mediaItems = (item.media || [])
                                .map((mediaUrl, mediaIndex) => ({
                                    type: isVideoUrl(mediaUrl) ? 'video' : 'image',
                                    src: mediaUrl,
                                    label: t('tufting.thumbnails.view', {
                                        title: item.title,
                                        index: mediaIndex + 1
                                    })
                                }))
                                .filter(Boolean);
                            const hasMultipleImages = mediaItems.length > 1;
                            const rawIndex = activeImageIndex[item.id] ?? 0;
                            const selectedIndex = mediaItems.length
                                ? ((rawIndex % mediaItems.length) +
                                      mediaItems.length) %
                                  mediaItems.length
                                : 0;
                            const mainMedia =
                                mediaItems[selectedIndex] || mediaItems[0];
                            const mainImage = mainMedia?.src;
                            const hasExternalLink =
                                item.buyLink && item.buyLink.startsWith('http');

                            return (
                                <article
                                    key={item.id}
                                    className='product-card reveal'
                                    style={{ '--delay': `${index * 0.06}s` }}
                                >
                                    <div className='product-media'>
                                        {mainImage ? (
                                            mainMedia?.type === 'video' ? (
                                                <video
                                                    src={mainImage}
                                                    autoPlay
                                                    loop
                                                    muted
                                                    playsInline
                                                    preload='metadata'
                                                />
                                            ) : (
                                                <img
                                                    src={mainImage}
                                                    alt={item.title}
                                                    className='picture-fill'
                                                />
                                            )
                                        ) : null}
                                        <div className='product-badge'>
                                            <span
                                                className={`status ${
                                                    item.isSold
                                                        ? 'sold'
                                                        : 'available'
                                                }`}
                                            >
                                                {item.isSold
                                                    ? t('tufting.status.sold')
                                                    : t(
                                                          'tufting.status.available'
                                                      )}
                                            </span>
                                        </div>
                                        {mediaItems.length ? (
                                            <button
                                                type='button'
                                                className='media-trigger'
                                                onClick={() =>
                                                    openLightbox(
                                                        mediaItems,
                                                        selectedIndex
                                                    )
                                                }
                                                aria-label={t(
                                                    'common.lightbox.openLabel'
                                                )}
                                            />
                                        ) : null}
                                    </div>
                                    <div
                                        className={`product-thumbs ${
                                            hasMultipleImages ? '' : 'is-empty'
                                        }`}
                                        aria-hidden={!hasMultipleImages}
                                    >
                                        {hasMultipleImages
                                            ? mediaItems.map((media, mediaIndex) => (
                                                  <button
                                                      key={`${item.id}-${mediaIndex}`}
                                                      type='button'
                                                      className={`thumb-button ${
                                                          selectedIndex ===
                                                          mediaIndex
                                                              ? 'active'
                                                              : ''
                                                      }`}
                                                      onClick={() =>
                                                          handleThumbnailClick(
                                                              item.id,
                                                              mediaIndex
                                                          )
                                                      }
                                                      aria-label={media.label}
                                                  >
                                                      {media.type === 'video' ? (
                                                          <video
                                                              src={media.src}
                                                              muted
                                                              playsInline
                                                              preload='metadata'
                                                          />
                                                      ) : (
                                                          <img
                                                              src={media.src}
                                                              alt={media.label}
                                                          />
                                                      )}
                                                  </button>
                                              ))
                                            : null}
                                    </div>
                                    <div className='product-body'>
                                        <div className='product-header'>
                                            <h3>{item.title}</h3>
                                            {!item.isSold ? (
                                                <span className='product-price'>
                                                    {getPriceLabel(item.price)}
                                                </span>
                                            ) : null}
                                        </div>
                                        <p className='product-meta'>
                                            {item.widthCm && item.heightCm
                                                ? `${item.widthCm} x ${item.heightCm} cm`
                                                : t(
                                                      'tufting.dimensionsFallback'
                                                  )}
                                        </p>
                                        {hasExternalLink && !item.isSold ? (
                                            <div className='product-actions'>
                                                <a
                                                    className='button button-outline'
                                                    href={item.buyLink}
                                                    target='_blank'
                                                    rel='noreferrer'
                                                >
                                                    {t('tufting.buy')}
                                                </a>
                                            </div>
                                        ) : null}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </section>

                <section className='section'>
                    <div className='container cta-panel'>
                        <div>
                            <p className='eyebrow'>
                                {t('tufting.cta.eyebrow')}
                            </p>
                            <h2>{t('tufting.cta.title')}</h2>
                            <p>{t('tufting.cta.note')}</p>
                        </div>
                        <div className='tufting-cta-actions'>
                            <a
                                className='button'
                                href='mailto:lucasmafrica57@gmail.com'
                                data-track-label='Tufting CTA email'
                            >
                                {t('tufting.cta.button')}
                            </a>
                            <button
                                type='button'
                                className='button button-outline'
                                onClick={handleOpenModal}
                                data-track-label='Open tufting estimate modal'
                            >
                                {t('modal.cta.primary')}
                            </button>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
            <div
                className={`toast ${isToastVisible ? 'show' : ''}`}
                role='status'
                aria-live='polite'
            >
                {toastMessage}
            </div>
            <TuftedRugModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSuccess={showToast}
            />
            <MediaLightbox
                open={lightboxState.open}
                mediaItems={lightboxState.items}
                activeIndex={lightboxState.index}
                onClose={closeLightbox}
                onNavigate={navigateLightbox}
            />
        </div>
    );
}

export default TuftingPage;
